import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../store";
import { Button } from "../../../../common/components/Button/Button";
import { PageTitleSection } from "../../../../common/components/PageTitleSection/PageTitleSection";
import "./MonthlyPerformanceIncome.scss";
import { IconInfoCircle } from "../../../../common/icons/IconInfoCircle/IconInfoCircle";
import { LeafNavigationTile } from "../../../../common/leaf/LeafNavigationTile/LeafNavigationTile";
import commonContent from "../../../../content/common";
import defaultContent from "../../../../content/performanceIncomeDashboard";
import { PerfIncomeKpiTile } from "../PerfIncomeKpiTile/PerfIncomeKpiTile";
import {
  FetchMonthlyIncomeBreakdownData,
  FetchMonthlyPerformanceIncomeData,
} from "./MonthlyPerformanceIncomeDataLayer";
import { useState, useEffect } from "react";
import { addParams } from "../../../../common/util/addParams";
import { ErrorTile } from "../../../../common/components/ErrorTile/ErrorTile";
import { PerformanceBonusTile } from "../PerformanceBonusTile/PerformanceBonusTile";
import PerformanceIncomeScheduleModal from "../PerformnaceIncomeScheduleModal/PerformanceIncomeScheduleModal";
import { IncomeBreakdown } from "../IncomeBreakdown/IncomeBreakdown";
import { CurrentEstimatedIncomeCard } from "../IncomeBreakdown/Components/CurrentEstimatedIncomeCard/CurrentEstimatedIncomeCard";
import { useMonthlyIncomeBreakdownMapper } from "./MonthlyIncomeBreakdownMapper";
import { maskMappedIncomeData } from "./MonthlyIncomeBreakdown.mask";
import { IncomeBreakdownLoader } from "../IncomeBreakdown/IncomeBreakdownLoader/IncomeBreakdownLoader";
import { RubyBonusModal } from "../PerformanceBonusTile/RubyBonusModal/RubyBonusModal";
import PersonalBonusModal from "../MonthlyPerformanceTotalModals/PersonalBonusModal/PersonalBonusModal";
import DifferentialBonusModal from "../MonthlyPerformanceTotalModals/DifferentialBonusModal/DifferentialBonusModal";
import { useGetPerfBonusDetailsQuery } from "../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI";
import { storedLocale } from "../../../Authentication/util";
import { AWARD_CODES } from "../../../../common/enums/award-codes";
import type {
  MonthlyIncomeBreakdownRubyBonusModal,
  MonthlyIncomeBreakdownCorePlanBonus,
} from "../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI.types";
import { getPerformanceYear } from "../../../../common/util/period";
import {
  trackMonthlyInfoIcon,
  trackPersonalBonusModal,
  trackDifferentialBonusModal,
  trackRubyBonusModal,
  trackMonthlyNavTileClick,
} from "../../../../common/tealium/PerformanceIncomeDashboard/PerformanceIncomeDashboardTrack";

export const MonthlyPerformanceIncome = () => {
  const { t } = useTranslation(["performanceIncomeDashboard", "common"]);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { configuration, user } = useAppSelector((state) => state.boot);
  const personaV2 = useAppSelector((state) => state.boot.shellConfig?.personaV2 ?? 1);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [showPersonalBonusModal, setShowPersonalBonusModal] = useState(false);
  const [showDifferentialBonusModal, setShowDifferentialBonusModal] = useState(false);
  const [showRubyBonusModal, setShowRubyBonusModal] = useState(false);
  const [rubyModalData, setRubyModalData] = useState<MonthlyIncomeBreakdownRubyBonusModal | null>(null);

  const getContentByPath = (path: string): string => {
    const keys = path.split(".");
    let value: any = defaultContent.incentives;

    for (const key of keys) {
      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        return path;
      }
    }

    return typeof value === "string" ? value : path;
  };

  const getTranslatedContentByPath = (path: string): string => {
    const defaultValue = getContentByPath(path);
    return String(t(`incentives.${path}`, defaultValue, { ns: "performanceIncomeDashboard" }));
  };

  const {
    monthlyPerformanceIncomeData,
    isLoading,
    errorStatus,
    refetch: refetchMonthlyPerformanceData,
  } = FetchMonthlyPerformanceIncomeData(selectedPeriod);

  const {
    monthlyIncomeBreakdownData,
    isIncomeBreakdownLoading,
    incomeBreakdownErrorStatus,
    refetch: refetchIncomeBreakdown,
  } = FetchMonthlyIncomeBreakdownData(selectedPeriod);

  const mappedIncomeData = maskMappedIncomeData(
    useMonthlyIncomeBreakdownMapper(monthlyIncomeBreakdownData, personaV2),
    useAppSelector((state) => state.showData.showData),
  );
  const performanceBonusData = monthlyIncomeBreakdownData?.CorePlanBonuses.find(
    (bonus: { AwardCode: number }) => bonus.AwardCode === AWARD_CODES.PERFORMANCE_BONUS,
  );

  const performanceBonusSubBonus = performanceBonusData?.SubBonuses?.find(
    (sub: { AwardCode: number }) => sub.AwardCode === AWARD_CODES.PERFORMANCE_BONUS_PERSONAL,
  );

  const differentialBonusSubBonus = performanceBonusData?.SubBonuses?.find(
    (sub: { AwardCode: number }) => sub.AwardCode === AWARD_CODES.PERFORMANCE_BONUS_DIFFERENTIAL,
  );

  const performanceBonusTotal = performanceBonusSubBonus
    ? {
        awardCode: performanceBonusSubBonus.AwardCode,
        incentiveTitle: getTranslatedContentByPath(performanceBonusSubBonus.ContentKey),
        tealiumTag: performanceBonusSubBonus.ContentKey.replace(/\./g, "_"),
        amount: performanceBonusSubBonus.BonusAmount,
      }
    : undefined;

  const differentialBonusTotal = differentialBonusSubBonus
    ? {
        awardCode: differentialBonusSubBonus.AwardCode,
        incentiveTitle: getTranslatedContentByPath(differentialBonusSubBonus.ContentKey),
        tealiumTag: differentialBonusSubBonus.ContentKey.replace(/\./g, "_"),
        amount: differentialBonusSubBonus.BonusAmount,
      }
    : undefined;

  const isFormattedValueGreaterThanZero = (formattedValue: string): boolean => {
    const numericValue = parseFloat(formattedValue.replace(/[^0-9.-]/g, ""));
    return !isNaN(numericValue) && numericValue > 0;
  };

  useEffect(() => {
    if (mappedIncomeData?.modalData) {
      const rubyModal = mappedIncomeData.modalData.find(
        (m: {
          type: string;
          data: MonthlyIncomeBreakdownCorePlanBonus | MonthlyIncomeBreakdownRubyBonusModal;
          index: number;
        }) => m.type === "ruby",
      );
      if (rubyModal) {
        setRubyModalData(rubyModal.data as MonthlyIncomeBreakdownRubyBonusModal);
      }
    }
  }, [mappedIncomeData]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === "#personal-bonus-modal") {
        setShowPersonalBonusModal(true);
        trackPersonalBonusModal(true);
      } else if (hash === "#differential-bonus-modal") {
        setShowDifferentialBonusModal(true);
        trackDifferentialBonusModal(true);
      } else if (hash === "#ruby-bonus-modal") {
        if (mappedIncomeData?.modalData) {
          const rubyModal = mappedIncomeData.modalData.find(
            (m: {
              type: string;
              data: MonthlyIncomeBreakdownCorePlanBonus | MonthlyIncomeBreakdownRubyBonusModal;
              index: number;
            }) => m.type === "ruby",
          );
          if (rubyModal) {
            setRubyModalData(rubyModal.data as MonthlyIncomeBreakdownRubyBonusModal);
            setShowRubyBonusModal(true);
            trackRubyBonusModal(true);
          }
        }
      }
    };

    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [mappedIncomeData]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");

      if (link && link.hash) {
        // Prevent default hash navigation and open modals directly
        if (link.hash === "#personal-bonus-modal") {
          e.preventDefault();
          setShowPersonalBonusModal(true);
          trackPersonalBonusModal(true);
        } else if (link.hash === "#differential-bonus-modal") {
          e.preventDefault();
          setShowDifferentialBonusModal(true);
          trackDifferentialBonusModal(true);
        } else if (link.hash === "#ruby-bonus-modal") {
          e.preventDefault();
          setShowRubyBonusModal(true);
          trackRubyBonusModal(true);
        }
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Prevent scrolling while any modal is open
  useEffect(() => {
    if (showRubyBonusModal || showPersonalBonusModal || showDifferentialBonusModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [showRubyBonusModal, showPersonalBonusModal, showDifferentialBonusModal]);

  const {
    data: perfBonusData,
    isFetching: isPerfBonusFetching,
    isError: isPerfBonusError,
    refetch: refetchPerfBonus,
  } = useGetPerfBonusDetailsQuery({
    affAbo: `${user.aff}-${user.abo}`,
    period: selectedPeriod,
    locale: storedLocale(),
    countryCode: user.isoCountryCode,
  });

  if (!monthlyPerformanceIncomeData) {
    return null;
  }

  const performanceBonus = monthlyPerformanceIncomeData?.PerformanceBonus ?? {
    gpvNeeded: {},
    isSpQualified: false,
    percentNeeded: {},
    perfBonusScaleData: {},
    performanceBonusPercent: {},
    qualifiedLegs: "",
    totalGpv: {},
  };

  const scaleData = monthlyPerformanceIncomeData?.PerformanceBonus?.perfBonusScaleData ?? {
    max: 0,
    progressValue: 0,
    ticks: [],
    milestones: [],
    legends: [],
  };
  const bonusDetails = monthlyPerformanceIncomeData?.PerformanceBonus?.schedules ?? [
    {
      volume: { Actual: 0, Formatted: "" },
      percent: { Actual: 0, Formatted: "" },
    },
  ];

  const handleShowInfoModal = () => {
    setShowPerformanceModal(true);
    addParams("performanceModal", "true", true, false);
    trackMonthlyInfoIcon(true);
  };

  const handleInfoModalClose = () => {
    setShowPerformanceModal(false);
    addParams("performanceModal", "false", false, true);
    trackMonthlyInfoIcon(false);
  };

  const handleClosePersonalBonusModal = () => {
    setShowPersonalBonusModal(false);
    trackPersonalBonusModal(false);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  };

  const handleCloseDifferentialBonusModal = () => {
    setShowDifferentialBonusModal(false);
    trackDifferentialBonusModal(false);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  };

  const handleCloseRubyBonusModal = () => {
    setShowRubyBonusModal(false);
    trackRubyBonusModal(false);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  };

  const renderMonthlyPerformanceTile = () => {
    if (errorStatus && !isLoading) {
      return (
        <ErrorTile
          clickFunction={refetchMonthlyPerformanceData}
          errorStatus={errorStatus}
          isLoading={isLoading}
          tealiumTrackId="Monthly_Performance_Tile"
        />
      );
    }

    return (
      <section>
        <PerformanceBonusTile
          totalGpv={monthlyPerformanceIncomeData?.PerformanceBonus?.totalGpv?.Formatted ?? ""}
          performanceBonusPercent={
            monthlyPerformanceIncomeData?.PerformanceBonus?.performanceBonusPercent?.Formatted ?? ""
          }
          perfBonusScaleData={scaleData}
          qualifiedLegs={performanceBonus.qualifiedLegs}
          isSpQualified={performanceBonus.isSpQualified}
          headerVariant={performanceBonus.headerVariant ?? ""}
          gpvNeeded={performanceBonus.gpvNeeded.Formatted}
          percentNeeded={String(performanceBonus.percentNeeded.Actual)}
          isLoading={isLoading}
        />
      </section>
    );
  };

  const getNavSubtitle = (contentKey: string, interpolations?: Record<string, string>): string => {
    const value = contentKey
      .split(".")
      .reduce<unknown>((acc, key) => (acc as Record<string, unknown>)?.[key], defaultContent.income);
    return `${t(`income.${contentKey}`, typeof value === "string" ? value : contentKey, { ns: "performanceIncomeDashboard", ...interpolations })}`;
  };

  const renderNavigationTiles = () => {
    if (!mappedIncomeData) return null;

    const tiles = mappedIncomeData.navigationItems
      .filter((item) => item.show)
      .map((item) => {
        const interpolations = item.id === "mbi" ? { performanceYear: getPerformanceYear(selectedPeriod) } : undefined;

        return (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- tracking wrapper, navigation handled by inner Link
          <div key={item.id} onClick={() => trackMonthlyNavTileClick(item.id)}>
            <LeafNavigationTile
              title={item.title}
              subtitle={getNavSubtitle(item.contentKey, interpolations)}
              iconType={item.iconType}
              to={item.to}
            />
          </div>
        );
      });

    return { tiles, count: tiles.length };
  };

  return (
    <div className="MonthlyPerformanceIncome" data-testid="monthly-performance-income">
      <div className="MonthlyPerformanceIncome__heading">
        <div className="MonthlyPerformanceIncome__heading-row">
          <PageTitleSection
            title={`${t("performance.title", defaultContent.performance.title as string)}`}
            infoButton={
              <Button
                aria-label={`${t("moreInfo", commonContent["moreInfo"], { ns: "common" })}`}
                className="MonthlyPerformanceIncome__heading-info-button"
                onClick={handleShowInfoModal}
                id="infoModalButton"
              >
                <IconInfoCircle />
              </Button>
            }
          />
        </div>
      </div>
      <div className="MonthlyPerformanceIncome__content">
        <section className="MonthlyPerformanceIncome__section MonthlyPerformanceIncome__section-content ">
          {renderMonthlyPerformanceTile()}
          <PerfIncomeKpiTile />
        </section>
      </div>

      {isIncomeBreakdownLoading && <IncomeBreakdownLoader isLoading={isIncomeBreakdownLoading} />}

      {incomeBreakdownErrorStatus && !isIncomeBreakdownLoading && (
        <ErrorTile
          clickFunction={refetchIncomeBreakdown}
          errorStatus={incomeBreakdownErrorStatus}
          isLoading={isIncomeBreakdownLoading}
          tealiumTrackId="Monthly_Income_Breakdown"
        />
      )}

      {mappedIncomeData && !isIncomeBreakdownLoading && !incomeBreakdownErrorStatus && (
        <IncomeBreakdown
          estimatedIncomeCard={<CurrentEstimatedIncomeCard value={mappedIncomeData.totalIncome} annual={false} />}
          corePlanTiles={mappedIncomeData.corePlanTiles}
          corePlanLeftTiles={mappedIncomeData.corePlanLeftTiles}
          corePlanRightTiles={mappedIncomeData.corePlanRightTiles}
          corePlusTiles={mappedIncomeData.corePlusTiles}
          bottomLinkCards={(() => {
            const navTilesData = renderNavigationTiles();
            if (!navTilesData) return null;
            return (
              <div
                className={`MonthlyPerformanceIncome__hyperlinks MonthlyPerformanceIncome__hyperlinks--count-${navTilesData.count}`}
              >
                {navTilesData.tiles}
              </div>
            );
          })()}
        />
      )}

      <PerformanceIncomeScheduleModal
        showPerformanceModal={showPerformanceModal}
        handleModalClose={handleInfoModalClose}
        bonusDetails={bonusDetails}
      />

      {performanceBonusTotal && (
        <PersonalBonusModal
          openModal={showPersonalBonusModal}
          onCloseModal={handleClosePersonalBonusModal}
          performanceData={perfBonusData || {}}
          isFetching={isPerfBonusFetching}
          isError={isPerfBonusError}
          onRetry={refetchPerfBonus}
        />
      )}

      {differentialBonusTotal && (
        <DifferentialBonusModal
          openModal={showDifferentialBonusModal}
          onCloseModal={handleCloseDifferentialBonusModal}
          performanceData={perfBonusData}
          performanceTotal={differentialBonusTotal}
          isFetching={isPerfBonusFetching}
          isError={isPerfBonusError}
          onRetry={refetchPerfBonus}
        />
      )}

      {rubyModalData && (
        <RubyBonusModal
          open={showRubyBonusModal}
          onClose={handleCloseRubyBonusModal}
          rubyBV={rubyModalData.rubyBV.formatted}
          isEarning={isFormattedValueGreaterThanZero(rubyModalData.rubyBonusTotal.formatted)}
          bonusTotal={rubyModalData.rubyBonusTotal.formatted}
          rubyMultiplier={rubyModalData.rubyMultiplier.formatted}
          rubyBVByCountry={rubyModalData.rubyBVByCountry}
        />
      )}
    </div>
  );
};
